
import { defineComponent } from "vue";
import { TableEditingConfig } from "@/components/controls/catalogueGrid/tableEditingConfig";
import { TableSelectionConfig } from "@/components/controls/catalogueGrid/tableSelectionConfig";
import { TableDesignConfig } from "@/components/controls/catalogueGrid/tableDesignConfig";
import { TextColumnConfig } from "@/components/controls/catalogueGrid/columns/textColumnConfig";
import { requiredRule } from "@/components/controls/catalogueGrid/columns/baseValidationRules";
import CatalogueGrid from "@/components/controls/catalogueGrid/CatalogueGrid.vue";
import planOrderNomenclatureCreateEditDialog from "@/components/journals/PlanOrderJournal/PlanOrderNomenclatureCreateEditDialog.vue";
import { DecimalColumnConfig } from "@/components/controls/catalogueGrid/columns/decimalColumnConfig";
import {
  getMarginResultTitle,
  getMarginMinResultTitle,
} from "@/components/journals/config";
import { IntegerColumnConfig } from "@/components/controls/catalogueGrid/columns/integerColumnConfig";
import api from "@/services/api";
import store from "@/store";

export default defineComponent({
  components: {
    CatalogueGrid,
  },
  props: {
    //показатель режима выбора
    selectMode: { required: false, type: Boolean, default: false },
    gridHeight: { required: false, type: String, default: null },
    //функция для получения выбранных элементов
    getSelectedData: { type: Function },
    //функция для вызова при изменении выбранных элементов
    changeSelectedData: { type: Function },
    //модель родительского элемента
    parentModel: { required: true },
    parentEditingConfig: { required: false, type: TableEditingConfig },
    updateParentModelCallback: { required: false },
  },
  methods: {
    async refreshParent() {
      if (this.updateParentModelCallback) this.updateParentModelCallback();
    },
    onRowPrepared(e: any) {
      var data = e.data;
      if (data) {
        if (data.volume <= data.loadedBeforeVolume + data.loadedVolume) {
          e.rowElement.bgColor = "#E5FFF0";
        } else if (
          data.volume > data.loadedBeforeVolume &&
          data.loadedVolume == 0
        ) {
          e.rowElement.bgColor = "#FFE5E5";
        }
      }
      if (e.rowType == "header") {
        e.rowElement.bgColor = "#E5F0FF";
      }
    },
    onSelectionChanged(e: any) {
      store.dispatch("loadedPackages/setLoadedPackages", { data: null });
    },
    async getLoadedPackages() {
      let selectedRows = this.$refs.grid.dxGrid.getSelectedRowsData();
      if (selectedRows.length == 0) return;
      const { data } = await api.get(
        "PlanOrderNomenclatureResult/GetLoadedPackages",
        {
          params: {
            id: selectedRows[0].id,
            idParent: this.parentModel.id,
          },
        }
      );
      const temp = {
        data: data,
      };
      store.dispatch("loadedPackages/setLoadedPackages", temp);
    },
  },
  computed: {
    planOrderNomenclatureCreateEditDialog: function () {
      return planOrderNomenclatureCreateEditDialog;
    },
  },
  data() {
    return {
      toolbarItems: [
        {
          location: "before",
          widget: "dxButton",
          options: {
            text: "Поиск пакетов",
            stylingMode: "outlined",
            onClick: this.getLoadedPackages,
          },
        },
      ],
      // подробности работы custom summary см. на сервере в методе SetCustomSummary
      summaryConfig: {
        totalItems: [
          {
            name: "TotalSummary",
            showInColumn: "name",
            displayFormat: "Итог:",
            summaryType: "custom",
          },
          {
            name: "VolumeMinSummary",
            showInColumn: "volumeMin",
            column: "volumeMin",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "VolumeWithoutMarginSummary",
            showInColumn: "volumeWithoutMargin",
            column: "volumeWithoutMargin",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "VolumeSummary",
            showInColumn: "volume",
            column: "volume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "LoadeBeforeVolumeSummary",
            showInColumn: "loadedBeforeVolume",
            column: "loadedBeforeVolume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "LoadedVolumeSummary",
            showInColumn: "loadedVolume",
            column: "loadedVolume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "RemainderSummary",
            showInColumn: "remainder",
            column: "remainder",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
          {
            name: "StorageRemainderSummary",
            showInColumn: "storageRemainder",
            column: "storageRemainder",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },
        ],
      },
      editingConfig: new TableEditingConfig({
        allowDeleteAll: false,
        allowUpdating: false,
        allowAdding: false,
        allowDeleting: false,
        confirmDelete: true,
        allowDataFill: false,
        allowExport: false,
        allowImport: false,
        allowClearFilter: false,
        allowDeleteMultipleRows: false,
        allowColumnReordering: true,
        mode: "row",
        chooseColumns: true,
        allowExportToExcel: true,
        exportedFileName: "Позиции заказа",
      }),
      selectModeEditingConfig: new TableEditingConfig({
        allowUpdating: false,
        allowAdding: false,
        allowDeleting: false,
        confirmDelete: false,
        allowDeleteMultipleRows: false,
      }),
      columns: [
        new TextColumnConfig({
          dataField: "name",
          caption: "Наименование",
          validationRules: [requiredRule],
        }),
        new IntegerColumnConfig({
          dataField: "containerCount",
          caption: "Кол-во контейнеров (шт)",
          allowEmpty: true,
        }),
        new DecimalColumnConfig({
          dataField: "volumeMin",
          caption: "Мин. объем (м³)",
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "volumeWithoutMargin",
          caption: "Объем (м³)",
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "volume",
          caption: "Макс. объем (м³)",
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "marginMin",
          caption: getMarginMinResultTitle((this.parentModel as any).planId),
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "margin",
          caption: getMarginResultTitle((this.parentModel as any).planId),
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "loadedBeforeVolume",
          caption: "Отгружено ранее (м³)",
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "loadedVolume",
          caption: "Погружено (м³)",
          decimalPoints: 0,
        }),
        new DecimalColumnConfig({
          dataField: "remainder",
          caption: "Остаток (м³)",
          decimalPoints: 1,
        }),
        new DecimalColumnConfig({
          dataField: "storageRemainder",
          caption: "Остаток на складе (м3)",
          decimalPoints: 1,
        }),
      ],
      designConfig: new TableDesignConfig({
        popUpColumnsCount: 1,
        popUpWidth: "850px",
        horizontalScroll: false,
      }),
      selectionConfig: new TableSelectionConfig({
        mode: "single",
      }),
      pageSizes: [10, 20, 50, 100],
    };
  },
  created() {
    if (this.selectMode) {
      this.editingConfig = this.selectModeEditingConfig;
    }
    if (this.gridHeight) {
      this.designConfig.height = this.gridHeight;
    }
  },
});
